import { reactive, toRefs, provide, inject } from "@vue/composition-api";
import { actions as appActions, state as appState } from "../../store/modules/app";
import { state as task_state, actions as task_actions } from "./task";
import Vue from "vue";
import axios from "axios";
import { state as accountState } from "@/store/modules/account";
import { actions as reportActions } from "./report";

const PROTOCOL = process.env.VUE_APP_WS_PROTOCOL ? process.env.VUE_APP_WS_PROTOCOL : "";
const HOST = process.env.VUE_APP_HOST ? process.env.VUE_APP_HOST : "";
const BASE_URL = `${PROTOCOL}://${HOST}`;

var timer_ws = null;

export const state = reactive({
    reconnect_counter: 0,
    time_entry_id: null,
    start: null,
    task_id: null,
    task_account_id: null,
    task_virtual_id: null,
    task_status: null,
    start_as_date: null,
    status: "stopped",
    can_be_started: false,
    total_duration: 0,
    running_duration: 0,
    ticking_duration: 0,
    billable: 0,
    task_initials: null,
    task_color: null,
    task_title: null,
    task_identity: null,
    task_client_name: null,
    task_topic_name: null,
    taskChangeCallbacks: [],
    timeEntryChangeCallbacks: []
});

let optimisticPreAction = function(action, task_id, task = null) {
    let orig_task = null;
    if (action === "complete" || action === "incomplete") {
        orig_task = task_actions.syncTaskChangesToLists(
            {
                id: task_id,
                virtual_id: task_id,
                status: action === "complete" ? "finished" : "in_progress"
            }
        );
        if (state.task_id === task_id.toString() || state.task_virtual_id === task_id.toString()) {
            state.task_status = action === "complete" ? "finished" : "in_progress";
            state.total_duration += state.running_duration + state.ticking_duration;
        }
    } else if (action === "start" || action === "pause") {
        if (task === null) {
            if (action === "start") {
                state.status = "running";
                state.start_as_date = new Date();
                state.ticking_duration = 0;
                state.can_be_started = true;
            } else {
                state.status = "stopped";
                state.can_be_started = true;
                state.total_duration += state.running_duration + state.ticking_duration;
                state.running_duration = 0;
                state.ticking_duration = 0;
            }
        } else {
            state.task_id = task.id ? task.id.toString() : task.virtual_id;
            state.task_virtual_id = task.virtual_id;
            state.task_status = "in_progress";
            if (action === "start") {
                state.status = "running";
                state.total_duration =
                    task.tracked_time && task.tracked_time[accountState.current_account.id.toString()] !== undefined
                        ? task.tracked_time[accountState.current_account.id.toString()]
                        : 0;
                state.running_duration = 0;
                state.start_as_date = new Date();
                state.ticking_duration = 0;
                state.can_be_started = true;
            } else {
                state.status = "stopped";
                state.can_be_started = true;
                state.total_duration += state.running_duration + state.ticking_duration;
                state.running_duration = 0;
                state.ticking_duration = 0;
            }
        }
    }
    return orig_task;
};

let executeAction = function(action, task_id, wait = false, task = null, description = null) {
    return new Promise(function(resolve, reject) {
        // update task list and timer right away (optimistically)
        let orig_task = null;
        if (!wait) orig_task = optimisticPreAction(action, task_id, task);

        // do action
        axios
            .post(Vue.config.url_prefix + "/api/timer/{0}".format(action), null, {
                params: {
                    task_id: task_id,
                    description: description
                }
            })
            .then(resp => resolve(resp.data))
            .catch(ex => {
                // if something went wrong put task back to list
                if (orig_task != null) task_actions.syncTaskAndRecurrenceChangesToLists(orig_task);
                reject(ex);
            });
    });
};

let updateTickingTime = function() {
    if (state.status !== "running" || state.start_as_date == null) return;
    let current_time = new Date() - state.start_as_date;
    if (current_time < 0) current_time = 0;
    state.ticking_duration = Math.floor(current_time / 1000);
};

let notifyTaskChange = (task) => {
    for (const callback of state.taskChangeCallbacks) {
        callback(task)
    }
}

let notifyTimeEntryChange = (timeEntry) => {
    for (const callback of state.timeEntryChangeCallbacks) {
        callback(timeEntry)
    }
}

let connectWs = function() {
    timer_ws = new WebSocket(`${BASE_URL}/ws/timer`);

    timer_ws.onopen = function() {
        console.log("Timer WebSocket: connected");
        state.reconnect_counter = 0;
    };

    timer_ws.onmessage = function(ev) {
        // console.log("Timer WebSocket: incoming message", JSON.parse(ev.data));
        let data = JSON.parse(ev.data);

        if (data.current != null && data.current.person_id === accountState.current_account.id) {
            state.task_id = data.current.task_id;
            state.task_virtual_id = data.current.task_virtual_id;
            state.task_status = data.current.task_status;
            state.status = data.current.status;
            state.task_initials = data.current.task_initials;
            state.task_color = data.current.task_color;
            state.task_title = data.current.task_title;
            state.task_topic_name = data.current.task_topic_name,
            state.task_client_name = data.current.task_client_name;
            state.task_identity = data.current.task_identity;
            state.total_duration = data.current.total_duration;
            state.running_duration = data.current.running_duration;
            state.can_be_started = data.current.can_be_started;
            state.start_as_date = new Date();
            state.ticking_duration = 0;
            state.task_account_id = data.current.task_account_id;
            state.start = data.current.start;
            state.time_entry_id = data.current.time_entry_id;
            reportActions.onWsTaskChange({ id: parseInt(data.current.task_id) });
            notifyTaskChange({ id: parseInt(data.current.task_id) })
        }

        if (state.task_id != null) {
            for (let i = 0; i < data.entries.length; i++) {
                let entry = data.entries[i];
                if (entry.person_id === accountState.current_account.id) {
                    if (state.task_id === entry.task_id) {
                        state.task_status = entry.task_status;
                        state.status = entry.status;
                        state.task_initials = entry.task_initials;
                        state.task_color = entry.task_color;
                        state.task_title = entry.task_title;
                        state.task_topic_name = entry.task_topic_name;
                        state.task_client_name = entry.task_client_name;
                        state.task_identity = entry.task_identity;
                        state.total_duration = entry.total_duration;
                        state.running_duration = entry.running_duration;
                        state.can_be_started = entry.can_be_started;
                        state.task_account_id = entry.task_account_id;
                        state.time_entry_id = entry.time_entry_id;
                        state.start = entry.start;
                        state.start_as_date = new Date();
                        state.ticking_duration = 0;
                    }
                    if (task_state.current_task != null && task_state.current_task.id === parseInt(entry.task_id)) {
                        if (task_state.current_task.tracked_time == null) task_state.current_task.tracked_time = {};
                        Vue.set(
                            task_state.current_task.tracked_time,
                            accountState.current_account.id.toString(),
                            entry.total_duration
                        );
                    }
                }
            }
        }

        for (let i = 0; i < data.entries.length; i++) {
            reportActions.onWsTaskChange({ id: parseInt(data.entries[i].task_id) });
            notifyTaskChange({ id: parseInt(data.entries[i].task_id) })
            notifyTimeEntryChange(data.entries[i])
        }
    };

    timer_ws.onclose = function() {
        console.log("Timer WebSocket: connection lost");
        if (state.reconnect_counter >= appState.reconnect_max_tries) {
            appActions.showGlobalConnectionLost();
        } else {
            state.reconnect_counter++;
        }
        setTimeout(function() {
            console.log("Timer WebSocket: reconnecting...({0})".format(state.reconnect_counter));
            connectWs();
        }, appState.reconnect_wait);
    };
};

const addTaskChangeCallback = (callback) => {
    state.taskChangeCallbacks.push(callback)
}

const addTimeEntryChangeCallback = (callback) => {
    state.timeEntryChangeCallbacks.push(callback)
}

export const actions = {
    addTimeEntryChangeCallback,
    connectWs: connectWs,
    executeAction: executeAction,
    updateTickingTime: updateTickingTime,
    addTaskChangeCallback: addTaskChangeCallback,
    fetchTimeEntries(task_id, all_users = 0) {
        return new Promise(function(resolve, reject) {
            axios
                .get(Vue.config.url_prefix + "/api/time_entries/", {
                    params: { task_id: task_id, all_users: all_users }
                })
                .then(function(resp) {
                    resolve(resp.data);
                })
                .catch(function(ex) {
                    reject(ex);
                });
        });
    },
    fetchLastTimeEntry() {
        return new Promise(function(resolve, reject) {
            axios
                .get(Vue.config.url_prefix + "/api/time_entries/last_time_entry/")
                .then(function(resp) {
                    resolve(resp.data);
                })
                .catch(function(ex) {
                    reject(ex);
                });
        });
    },
    deleteTimeEntry(time_entry_id) {
        return new Promise(function(resolve, reject) {
            axios
                .delete(Vue.config.url_prefix + "/api/time_entries/{0}".format(time_entry_id))
                .then(function() {
                    resolve();
                })
                .catch(function(ex) {
                    reject(ex);
                });
        });
    },
    saveTimeEntry(data) {
        return new Promise(function(resolve, reject) {
            axios
                .post(Vue.config.url_prefix + "/api/time_entries/{0}".format(data.id > 0 ? data.id : ""), data)
                .then(function(resp) {
                    resolve(resp.data);
                })
                .catch(function(ex) {
                    reject(ex);
                });
        });
    },
    onWsTaskChange(t) {
        if (state.task_id == null) return;
        if (state.task_id === t.id || state.task_virtual_id === t.virtual_id) {
            state.task_status = t.status
            state.task_initials = t.client ? t.client.client_initials : null
            state.task_color = t.client ? t.client.color : null
            state.task_title = t.title
            state.task_client_name = t.client ? t.client.name : null
            state.task_topic_name = t.topic ? t.topic.name : null
        }
    },
    onWsTaskDelete(fx_equals) {
        if (state.task_id == null) return;
        let tmp = { id: state.task_id };
        if (state.task_virtual_id != null) tmp.virtual_id = state.task_virtual_id;
        if (fx_equals(tmp)) {
            state.task_id = null;
            state.task_virtual_id = null;
        }
    }
};

export const key = Symbol("TimerModule");

export const provideTimer = () => {
    provide(key, {
        ...toRefs(state),
        ...actions
    });
};

appState.web_socket_connectors.push(actions.connectWs);
setInterval(updateTickingTime, 1000);

export default () => inject(key);
